import { createSlice, createAsyncThunk, SerializedError } from '@reduxjs/toolkit';
import {
  ILessonsCatalog,
  ICoursesCatalog,
  tSkills,
  tCatalogItems,
  tCatalogStatus
} from 'common/modules/ReactShared/data/models';
import { addItemProp, mixArraysWithStep } from 'common/modules/ReactShared/utils';
import { CatalogApiTypes } from 'common/modules/ReactShared/data/api';
import { tSharedAppState } from '../types';
import { isRequestCanceled } from '../../api/catalog/api';
import { catalogApi } from '../../api';

interface RejectedAction {
  type: string;
  payload: undefined;
  error: SerializedError | any;
}

interface ICatalogState {
  catalogItems: tCatalogItems;
  lessonsCatalog: ILessonsCatalog | null;
  coursesCatalog: ICoursesCatalog | null;
  skills: tSkills;
  status: tCatalogStatus;
}

const initialState: ICatalogState = {
  catalogItems: [],
  lessonsCatalog: null,
  coursesCatalog: null,
  skills: [],
  status: 'initialEmpty'
};

const getCatalogItems = createAsyncThunk<
  {
    catalogItems: tCatalogItems;
    lessonsCatalog: ILessonsCatalog | null;
    coursesCatalog: ICoursesCatalog | null;
  },
  string,
  { rejectValue: RejectedAction }
>('catalog/getCoursesCatalogItems_request', async (filterUrlString) => {
  return _fetchCatalog(filterUrlString);
});

const getCatalogMore = createAsyncThunk<
  {
    catalogItems: tCatalogItems;
    lessonsCatalog: ILessonsCatalog | null;
    coursesCatalog: ICoursesCatalog | null;
  } | null,
  string,
  { state: tSharedAppState }
>('catalog/getCatalogMore_request', async (filterUrlString, { getState }) => {
  const coursesPageNumber = getState().shared.catalog.coursesCatalog?.nextPage;
  const lessonsPageNumber = getState().shared.catalog.lessonsCatalog?.nextPage;
  const pageNumber = coursesPageNumber || lessonsPageNumber;

  if (!pageNumber) return null;

  return _fetchCatalog(filterUrlString, {
    coursesPageNumber,
    lessonsPageNumber
  });
});

const getSkills = createAsyncThunk<tSkills, void>(
  'catalog/getSkills_request',
  async (_) => {
    const { skills } = await catalogApi.fetchSkills();

    return skills;
  }
);

const catalogSlice = createSlice({
  name: 'catalog',
  initialState: initialState as ICatalogState,
  reducers: {
    resetCatalog: () => initialState
  },
  extraReducers: (builder) => {
    builder.addCase(getCatalogItems.pending, (state) => {
      state.status = 'requesting';
    });

    builder.addCase(getCatalogItems.fulfilled, (state, action) => {
      const { coursesCatalog, lessonsCatalog, catalogItems } = action.payload;
      state.coursesCatalog = coursesCatalog;
      state.lessonsCatalog = lessonsCatalog;
      state.catalogItems = catalogItems;
      state.status = catalogItems.length ? 'hasResult' : 'noResult';
    });

    builder.addCase(getCatalogItems.rejected, (state, action) => {
      const { error } = action;

      if (error && !isRequestCanceled(error)) {
        state.coursesCatalog = initialState.coursesCatalog;
        state.lessonsCatalog = initialState.lessonsCatalog;
        state.catalogItems = initialState.catalogItems;
        state.status = 'noResult';
      }
    });

    builder.addCase(getCatalogMore.fulfilled, (state, action) => {
      const { payload } = action;
      if (payload) {
        state.coursesCatalog = payload.coursesCatalog;
        state.lessonsCatalog = payload.lessonsCatalog;
        state.catalogItems.splice(state.catalogItems.length, 0, ...payload.catalogItems);
        state.status = 'hasResult';
      }
    });

    builder.addCase(getSkills.fulfilled, (state, action) => ({
      ...state,
      skills: action.payload
    }));
  }
});

const { reducer: catalogReducer, actions } = catalogSlice;

const catalogActions = {
  getCatalogItems,
  getCatalogMore,
  getSkills,
  ...actions
};

export { catalogActions };
export default catalogReducer;
export type { ICatalogState };

type tPageNumber = {
  lessonsPageNumber?: number | null;
  coursesPageNumber?: number | null;
};

async function _fetchCatalog(filterUrlString: string, pageNumber?: tPageNumber) {
  let coursesCatalog: ICoursesCatalog | null = null;
  let lessonsCatalog: ILessonsCatalog | null = null;

  if (pageNumber?.coursesPageNumber && !pageNumber.lessonsPageNumber) {
    const resp = await catalogApi.fetchCoursesCatalog(
      filterUrlString,
      pageNumber.coursesPageNumber
    );
    coursesCatalog = resp.coursesCatalog;
  } else if (pageNumber?.lessonsPageNumber && !pageNumber.coursesPageNumber) {
    const resp = await catalogApi.fetchLessonsCatalog(
      filterUrlString,
      pageNumber.lessonsPageNumber
    );
    lessonsCatalog = replaceZeroLevelInLessons(resp.lessonsCatalog);
  } else {
    const [coursesResponse, lessonsResponse] = await Promise.allSettled([
      catalogApi.fetchCoursesCatalog(filterUrlString, pageNumber?.coursesPageNumber),
      catalogApi.fetchLessonsCatalog(filterUrlString, pageNumber?.lessonsPageNumber)
    ]);
    if (coursesResponse.status === 'fulfilled') {
      coursesCatalog = coursesResponse.value.coursesCatalog;
    }
    if (lessonsResponse.status === 'fulfilled') {
      lessonsCatalog = replaceZeroLevelInLessons(lessonsResponse.value.lessonsCatalog);
    }
  }

  return {
    lessonsCatalog,
    coursesCatalog,
    catalogItems: mixArraysWithStep(
      addItemProp(lessonsCatalog?.lessons, 'type', 'lesson'),
      addItemProp(coursesCatalog?.courses, 'type', 'course'),
      4
    ) as tCatalogItems
  };
}

function replaceZeroLevelInLessons(
  lessonsCatalog: CatalogApiTypes.ILessonsCatalog
): ILessonsCatalog {
  return {
    ...lessonsCatalog,
    lessons: lessonsCatalog.lessons.map((lesson) => ({
      ...lesson,
      level: lesson.level === 'zero_level' ? 'a1' : lesson.level
    }))
  };
}
